import * as moment from 'moment';

import { push } from 'react-router-redux';
import { Dispatch } from 'redux';

import * as AccountRepository from 'repositories/account';

import { AccountActionCreators } from 'actions/account';
import { AppActionCreators } from 'actions/app';
import { LayoutActionCreators } from 'actions/layout';
import { AccountProfileStatus } from 'constants/account';
import { ROUTE_ACCOUNT_GET_STARTED, ROUTE_ACCOUNT_KYC_VERIFICATION_SUCCESS, ROUTE_ACCOUNT_PHONE_SET, ROUTE_LOGIN } from 'constants/app';
import { HttpStatusCode, INVALID_GRANT } from 'constants/error';
import { destroySession, initSession } from 'helper/auth';
import { handleGenericApiError } from 'helper/error';
import { Account, AccountLoginForm, AccountLoginHistoryList, AccountProfile } from 'models/account';
import { ErrorResponse } from 'models/error';
import { Thunk } from 'models/redux';

export function login(formValue: AccountLoginForm): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());

    initSession(formValue.username, formValue.password)
      .then(() => {
        dispatch(AccountActionCreators.setIsAuthenticated.create());
      })
      .then(() => {
        return AccountRepository.get()
          .then((account) => {
            dispatch(AccountActionCreators.updateAccount.create(account));
            if (!account.phone) {
              dispatch(push(ROUTE_ACCOUNT_PHONE_SET));
              dispatch(LayoutActionCreators.hideLoading.create());
            } else {
              dispatch(redirectByLoginHistory(account));
            }
          });
      })
      .catch((e) => {
        handleAccountAuthenticationFailed(e, dispatch);
      });
  };
}

export function loginWithRedirect(formValue: AccountLoginForm, redirectTo: string): Thunk {
  return (dispatch) => {
    initSession(formValue.username, formValue.password)
      .then(() => {
        window.location.href = redirectTo;
      })
      .catch((e) => {
        handleAccountAuthenticationFailed(e, dispatch);
      });
  };
}

export function logout(): Thunk {
  return (dispatch) => {
    destroySession();
    dispatch(AppActionCreators.resetAllState.create());
    dispatch(push(ROUTE_LOGIN));
  };
}

export function checkLoginStatus(): Thunk {
  return (dispatch) => {
    AccountRepository.get()
      .then((account) => {
        dispatch(AccountActionCreators.updateAccount.create(account));
        return false;
      })
      .catch((e: ErrorResponse) => {
        return new Promise((resolve, reject) => {
          if (e.status === HttpStatusCode.UNAUTHORIZED
            || (e.status === HttpStatusCode.BAD_REQUEST && e.body.error === INVALID_GRANT)
          ) {
            resolve(true);
          } else {
            reject();
          }
        });
      })
      .then((isLoggedOut: boolean) => {
        if (isLoggedOut) {
          destroySession();
          window.location.reload(true);
        }
      })
      .catch((e) => {
        console.error(e); // tslint:disable-line
      });
  };
}

function handleAccountAuthenticationFailed(error: ErrorResponse, dispatch: Dispatch<void>) {
  handleGenericApiError('action.failed.account.login', error, dispatch);
  dispatch(LayoutActionCreators.hideLoading.create());
  console.error(error); // tslint:disable-line
}

function redirectByLoginHistory(account: Account): Thunk {
  return (dispatch) => {
    AccountRepository.getLoginHistories()
      .then((accountLoginHistoryList) => {
        const isFirstTimeLogin = accountLoginHistoryList.page.totalElements === 1;
        if (isFirstTimeLogin) {
          dispatch(push(ROUTE_ACCOUNT_GET_STARTED));
        } else if (isAfterKycVerified(accountLoginHistoryList, account.profile)) {
          dispatch(push(ROUTE_ACCOUNT_KYC_VERIFICATION_SUCCESS));
        }
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((error) => {
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(error); // tslint:disable-line
      });
  };
}

function isAfterKycVerified(accountLoginHistoryList: AccountLoginHistoryList, profile?: AccountProfile) {
  if (profile) {
    const lastLoginTimeAfterVerified = accountLoginHistoryList.content[0].createdAt;
    const lastLoginTimeBeforeVerified = accountLoginHistoryList.content[1].createdAt;
    return profile.status === AccountProfileStatus.VERIFIED && moment(profile.verifiedAt).isBetween(lastLoginTimeBeforeVerified, lastLoginTimeAfterVerified);
  }

  return false;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/account/login.ts